import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {FormsModule, NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {ActivatedRoute, Router} from '@angular/router';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {DateTime} from 'luxon';
import {TableComponent} from '../../../../components/table/table.component';
import {takeUntil} from 'rxjs';
import {TableFunctionList} from '../../../../entities/table-function-list.entity';
import {CustomTableFunctionTypeEnum} from '../../../../enums/CustomTableFunctionTypeEnum';
import {Backend} from '@generated-backend-types';
import {MonthPickerComponent} from '../month-picker/month-picker.component';
import {applyTimeframeQueryParams, TIMEFRAME_MONTH_PARAM, TIMEFRAME_YEAR_PARAM} from '../../../../utils/timeframe';
import TimeTrackingOverviewByYear = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingOverview.TimeTrackingOverviewByYear;

@Component({
  selector: 'app-authenticated-time-tracking-overview-index',
  templateUrl: './index.component.html',
  imports: [FormsModule, ReactiveFormsModule, TableComponent, TranslatePipe, MonthPickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class IndexComponent extends AuthenticatedBaseComponent implements OnInit {
  public view: 'month' | 'year' = 'month';
  public timeframeForm: StrictFormGroup<{ year: string, month: string }> = this.fb.group({
    year: this.fb.control(''),
    month: this.fb.control(''),
  });
  public reloadTable$ = new EventEmitter<void>();
  public globalFilter?: Record<string, string>;
  public tableFunctionList: TableFunctionList = {
    custom: rowData => {
      return {
        type: CustomTableFunctionTypeEnum.BUTTON,
        action: () => this.router.navigate(['time-tracking', 'overview', (rowData as TimeTrackingOverviewByYear).time_tracking_employee_id]),
        iconClass: 'fa-eye text-primary',
      };
    }
  }

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: NonNullableFormBuilder,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public get tableId(): string {
    switch (this.view) {
      case "month":
        return 'dashboard.time-tracking.overview.month.index';
      case "year":
        return 'dashboard.time-tracking.overview.year.index';
    }
  }

  public setView(view: 'month' | 'year') {
    this.router.navigate(['time-tracking', 'overview', view], {queryParams: this.getQueryParams(view)}).then();
  }

  public ngOnInit() {
    this.titleService.setTitle('authenticated.time_tracking.overview.index.title');

    this.activatedRoute.data.subscribe(data => this.view = data['view']);

    this.activatedRoute.queryParams.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      this.timeframeForm.patchValue({
        month: params[TIMEFRAME_MONTH_PARAM] ?? DateTime.now().month.toString(),
        year: params[TIMEFRAME_YEAR_PARAM] ?? DateTime.now().year.toString(),
      });

      this.setGlobalFilter();
    });

    this.timeframeForm.valueChanges.subscribe(() => {
      this.router.navigate([], {queryParams: this.getQueryParams(this.view)}).then();
    });
  }

  private getQueryParams(view: 'month' | 'year') {
    return applyTimeframeQueryParams(
      this.activatedRoute.snapshot.queryParams,
      {year: this.timeframeForm.controls.year.value, month: this.timeframeForm.controls.month.value},
      view !== 'year',
    );
  }

  private setGlobalFilter() {
    const filter: { year: string, month?: string } = {year: this.timeframeForm.controls.year.value};

    if (this.view === 'month') {
      filter['month'] = this.timeframeForm.controls.month.value;
    }

    this.globalFilter = filter;

    // Flush the [globalFilter] input to the table before triggering its reload,
    // so it fetches with the new filter (replaces a fragile setTimeout race).
    this.cdr.detectChanges();
    this.reloadTable$.emit();
  }
}
